import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSelaProject, runSelaProjectIntel, type SelaProjectDetail } from '../../services/selaApi';
import { recordPageDwell } from '../../utils/rum';
import {
  recordV2ErrorStage,
  recordV2FunnelStep,
  recordV2Latency,
  recordV2PageView,
  resolveWorkspacePage,
} from '../../utils/v2Rum';
import { AskSelaProvider, useAskSela } from './ask-sela/AskSelaContext';
import { SelaProjectSidebar } from './SelaProjectSidebar';
import { SelaUserMenu } from './SelaUserMenu';

const SITE_POLL_INTERVAL_MS = 8000;

function shouldPollForPrimarySite(
  primarySite: SelaProjectDetail['primarySite'],
  intelKickoffInProgress: boolean,
): boolean {
  if (primarySite?.geoLayers?.length) {
    return false;
  }
  if (!primarySite) {
    return true;
  }
  return intelKickoffInProgress;
}

function GlobalAskSelaButton() {
  const { openAskSela } = useAskSela();
  return (
    <button
      type="button"
      onClick={() => openAskSela(undefined)}
      className="rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-sm font-semibold text-[#1e4474] shadow-sm hover:border-[#3251b7] hover:bg-[#eff2fe]"
    >
      Ask Sela
    </button>
  );
}

export function SelaProjectWorkspaceLayout() {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [project, setProject] = useState<SelaProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [intelKickoffInProgress, setIntelKickoffInProgress] = useState(false);
  const [intelKickoffError, setIntelKickoffError] = useState<string | null>(null);
  const hasStartedInitialResearchRef = useRef(false);
  const hasTrackedWorkspaceEnterRef = useRef(false);
  const pageDwellStartedAtRef = useRef(performance.now());
  const currentWorkspacePageRef = useRef('overview');

  const loadProject = useCallback(
    async (showLoading: boolean) => {
      if (!projectId) {
        setLoading(false);
        setProject(null);
        return;
      }

      const startedAt = performance.now();
      if (showLoading) {
        recordV2FunnelStep('project_load', 'start', {}, projectId);
      }

      try {
        if (showLoading) {
          setLoading(true);
        }
        if (showLoading) {
          setError(null);
        }
        const nextProject = await getSelaProject(projectId);
        setProject(nextProject);
        if (showLoading) {
          recordV2FunnelStep('project_load', 'success', {}, projectId);
          recordV2Latency('project_load', performance.now() - startedAt, {}, projectId);
        }
      } catch (err) {
        if (showLoading) {
          setError(err instanceof Error ? err.message : 'Failed to load project.');
          recordV2FunnelStep('project_load', 'failure', {}, projectId);
          recordV2ErrorStage('project_load', err, {}, projectId);
        }
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [projectId],
  );

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      setProject(null);
      return;
    }
    void loadProject(true);
  }, [loadProject, projectId]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const workspacePage = resolveWorkspacePage(location.pathname);
    const pageName = `workspace_${workspacePage}`;
    pageDwellStartedAtRef.current = performance.now();
    currentWorkspacePageRef.current = workspacePage;

    recordV2PageView(pageName, { route: location.pathname }, projectId);
    recordV2FunnelStep(pageName, 'shown', { route: location.pathname }, projectId);

    if (!hasTrackedWorkspaceEnterRef.current) {
      hasTrackedWorkspaceEnterRef.current = true;
      recordV2FunnelStep('workspace_enter', 'shown', { route: location.pathname }, projectId);
    }

    return () => {
      recordPageDwell(pageName, performance.now() - pageDwellStartedAtRef.current, {
        funnel_id: 'v2',
        project_id: projectId,
        route: location.pathname,
        page: pageName,
      });
    };
  }, [location.pathname, projectId]);

  useEffect(() => {
    const reportStatus = project?.primarySite?.report?.status;
    if (!projectId || !reportStatus || !['queued', 'researching', 'generating'].includes(reportStatus)) {
      return;
    }

    const pollId = window.setInterval(() => {
      void loadProject(false);
    }, 5000);

    return () => {
      window.clearInterval(pollId);
    };
  }, [loadProject, project?.primarySite?.report?.status, projectId]);

  useEffect(() => {
    if (!projectId || !shouldPollForPrimarySite(project?.primarySite, intelKickoffInProgress)) {
      return;
    }

    const pollId = window.setInterval(() => {
      void loadProject(false);
    }, SITE_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(pollId);
    };
  }, [intelKickoffInProgress, loadProject, project?.primarySite, projectId]);

  useEffect(() => {
    const shouldStartResearch = Boolean((location.state as { startProjectResearch?: boolean } | null)?.startProjectResearch);
    if (!projectId || !shouldStartResearch || hasStartedInitialResearchRef.current) {
      return;
    }

    hasStartedInitialResearchRef.current = true;
    let isMounted = true;
    const startedAt = performance.now();
    recordV2FunnelStep('intel_kickoff', 'start', {}, projectId);
    setIntelKickoffInProgress(true);
    setIntelKickoffError(null);

    void runSelaProjectIntel(projectId)
      .then((nextProject) => {
        if (!isMounted) {
          return;
        }
        setProject(nextProject);
        recordV2FunnelStep('intel_kickoff', 'success', {
          report_status: nextProject.primarySite?.report?.status,
        }, projectId);
        recordV2Latency('intel_kickoff', performance.now() - startedAt, {}, projectId);
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setIntelKickoffError(err instanceof Error ? err.message : 'Failed to start project research.');
        recordV2FunnelStep('intel_kickoff', 'failure', {}, projectId);
        recordV2ErrorStage('intel_kickoff', err, {}, projectId);
      })
      .finally(() => {
        if (!isMounted) {
          return;
        }
        setIntelKickoffInProgress(false);
        navigate(location.pathname, { replace: true });
      });

    return () => {
      isMounted = false;
    };
  }, [location.pathname, location.state, navigate, projectId]);

  if (!projectId) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#f7fafd] text-sm text-[#475669]">Loading project...</div>;
  }

  if (error || !project) {
    return <Navigate to="/" replace />;
  }

  return (
    <AskSelaProvider project={project} intelKickoffInProgress={intelKickoffInProgress}>
      <div className="flex min-h-screen bg-[#f7fafd]">
        <SelaProjectSidebar
          projectId={projectId}
          currentPage={resolveWorkspacePage(location.pathname)}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center justify-between border-b border-[#e7e9ee] bg-white px-6 md:px-8">
            <div className="flex min-w-0 items-center gap-2">
              <span className="max-w-[min(100%,280px)] truncate rounded-md border border-[#e7e9ee] bg-slate-50/80 px-3 py-1.5 text-sm font-medium text-[#383e4d]">
                {project.title}
              </span>
              {project.locationMode === 'parcel' ? (
                <span
                  className="shrink-0 rounded-md border border-[#fde68a] bg-[#fef3c7] px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#92400e]"
                  title={`Parcel ${project.parcelPin || ''}${project.city ? ` (${project.city}, ${(project.state || '').toUpperCase()})` : ''}`.trim()}
                >
                  Parcel
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <GlobalAskSelaButton />
              <SelaUserMenu />
            </div>
          </header>
          <Outlet context={{ project, intelKickoffInProgress, intelKickoffError }} />
        </div>
      </div>
    </AskSelaProvider>
  );
}
